import intersection from 'lodash/intersection';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { createResourceLocatorString, matchPathname } from '../../util/routes';
import {
  isAnyFilterActive,
  parseSelectFilterOptions,
  constructQueryParamName,
} from '../../util/search';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import {
  getStartOf,
  parseDateFromISO8601,
  subtractTime,
  addTime,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isFieldForCategory, isFieldForListingType } from '../../util/fieldHelpers';
import { ORIGIN_COUNTRY_CODES, ORIGIN_COUNTRY_NONE } from '../../util/originCountry';
import { SEARCH_BREADCRUMB_ROOT_LABEL } from './CategorySeo/searchRouteProps';

/**
 * Sanity ceiling for the price range in the URL, in whole euros. The slider's
 * real ceiling is dynamic (most expensive matching listing, rounded up to the
 * next thousand), so there is no meaningful static maximum to clamp against —
 * this only keeps hand-edited or corrupted URLs from reaching the API.
 */
const PRICE_FILTER_ABSOLUTE_MAX_EUR = 10000000;

const validURLParamForCategoryData = (prefix, categories, level, params) => {
  const levelKey = constructQueryParamName(`${prefix}${level}`, 'public');
  const levelValue = params?.[levelKey];
  const foundCategory = categories.find(cat => cat.id === params?.[levelKey]);
  const subcategories = foundCategory?.subcategories || [];
  return foundCategory && subcategories.length > 0
    ? {
        [levelKey]: levelValue,
        ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
      }
    : foundCategory
    ? { [levelKey]: levelValue }
    : {};
};

const validURLParamForListingTypeData = (listingTypes, param) => {
  const listingTypeValue = param?.pub_listingType;
  const foundListingType = listingTypes.find(lt => lt === listingTypeValue);
  return foundListingType && listingTypeValue ? { pub_listingType: listingTypeValue } : {};
};

/**
 * Omit those listing field parameters, that are not allowed with current category selection
 *
 * @param {Object} searchParams current search params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @returns search parameters without currently restricted listing fields
 */
export const omitLimitedListingFieldParams = (searchParams, filterConfigs) => {
  const {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams = {},
  } = filterConfigs;

  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const listingTypeSearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'listingType');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, searchParams)
    : {};

  const validListingTypeParamNames =
    activeListingTypes && listingTypeSearchConfig
      ? validURLParamForListingTypeData(activeListingTypes, searchParams)
      : {};

  return Object.entries(searchParams).reduce((picked, searchParam) => {
    const [searchParamKey, searchParamValue] = searchParam;
    const foundConfig = listingFieldsConfig.find(
      f => constructQueryParamName(f.key, f.scope) === searchParamKey
    );
    const currentCategories = Object.values(validNestedCategoryParamNames);
    const isForCategory = isFieldForCategory(currentCategories, foundConfig);
    const currentListingType = Object.values(validListingTypeParamNames);
    const isForListingType = isFieldForListingType(currentListingType, foundConfig);
    const searchParamMaybe =
      !foundConfig || (foundConfig && isForCategory && isForListingType)
        ? { [searchParamKey]: searchParamValue }
        : {};
    return { ...picked, ...searchParamMaybe };
  }, {});
};

/**
 * Validates a filter search param against the default and extended data configuration of listings.
 *
 * All invalid param names and values are dropped
 *
 * @param {String} queryParamName Search parameter name
 * @param {Object} paramValue Search parameter value
 * @param {Object} listingFieldFilters extended data configuration with indexForSearch === true
 * @param {Object} defaultFilters configuration for default built-in filters.
 */
export const validURLParamForExtendedData = (
  queryParamName,
  paramValueRaw,
  listingFieldFilters,
  defaultFilters
) => {
  const paramValue = paramValueRaw.toString();

  // Price is built-in filter for listing entities
  if (queryParamName === 'price') {
    // The range is in whole euros (see PRICE_FILTER_ABSOLUTE_MAX_EUR). It is
    // deliberately NOT clamped to the configured max: that value is only a
    // fallback ceiling for the slider, while the real ceiling is derived from
    // the most expensive matching listing. Clamping here would truncate every
    // range above 1000 EUR. The floor is always 0.
    const values = paramValue ? paramValue.split(',') : [];
    if (values.length !== 2) {
      return {};
    }
    const [minValue, maxValue] = values.map(v => Number.parseInt(v, 10));
    const isSane = value => Number.isInteger(value) && value <= PRICE_FILTER_ABSOLUTE_MAX_EUR;
    if (!isSane(minValue) || !isSane(maxValue)) {
      return {};
    }
    return { [queryParamName]: [Math.max(0, minValue), Math.max(0, maxValue)].join(',') };
  } else if (queryParamName === 'keywords') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'dates') {
    const searchTZ = 'Etc/UTC';
    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const dates = paramValue ? paramValue.split(',') : [];
    const hasValues = dates.length > 0;
    const startDate = hasValues ? parseDateFromISO8601(dates[0], searchTZ) : null;
    const endDate = hasValues ? parseDateFromISO8601(dates[1], searchTZ) : null;
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    return hasValidDates ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'seats') {
    return paramValue ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'pub_listingType') {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  } else if (queryParamName === 'pub_originCountry') {
    // Origin country is a built-in (default) filter backed by top-level
    // publicData.originCountry. Keep only curated ISO codes, plus the "none"
    // sentinel that stands for an applied empty selection (matches nothing).
    if (paramValue === ORIGIN_COUNTRY_NONE) {
      return { [queryParamName]: paramValue };
    }
    const valueArray = parseSelectFilterOptions(paramValue);
    const validValues = intersection(valueArray, ORIGIN_COUNTRY_CODES).join(',');
    return validValues.length > 0 ? { [queryParamName]: validValues } : {};
  }

  // Resolve configurations for extended data filters
  const listingFieldFilterConfig = listingFieldFilters.find(
    f => queryParamName === constructQueryParamName(f.key, f.scope)
  );

  if (listingFieldFilterConfig) {
    const { schemaType, enumOptions = [], filterConfig } = listingFieldFilterConfig;
    if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType)) {
      const isSchemaTypeMultiEnum = schemaType === SCHEMA_TYPE_MULTI_ENUM;
      const searchMode = filterConfig?.searchMode;

      // Pick valid select options only
      const valueArray = parseSelectFilterOptions(paramValue);
      const allowedValues = enumOptions.map(o => `${o.option}`);
      const validValues = intersection(valueArray, allowedValues).join(',');

      return validValues.length > 0
        ? {
            [queryParamName]:
              isSchemaTypeMultiEnum && searchMode ? `${searchMode}:${validValues}` : validValues,
          }
        : {};
    } else {
      // Generic filter - remove empty params
      return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
    }
  }
  return {};
};

/**
 * Checks filter param value validity.
 *
 * The URL params that are not part of listing.query filters are dropped by default.
 *
 * @param {Object} params Search query params
 * @param {Object} filterConfigs contains listingFieldsConfig and defaultFiltersConfig.
 * @param {boolean} dropNonFilterParams if false, extra params are passed through.
 */
export const validFilterParams = (params, filterConfigs, dropNonFilterParams = true) => {
  const { listingFieldsConfig, defaultFiltersConfig, listingCategories } = filterConfigs;

  const listingFieldFiltersConfig = listingFieldsConfig.filter(
    config => config.filterConfig?.indexForSearch
  );
  const listingFieldParamNames = listingFieldFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  // Note: builtInFilterParamNames might include categoryLevel,
  //       even though it isn't a paramname that's used with nested category tree.
  //       (pub_categoryLevel1, pub_categoryLevel2, and pub_categoryLevel3 are used instead.)
  const builtInFilterParamNames = defaultFiltersConfig.map(f => {
    return ['category', 'listingType', 'originCountry'].includes(f.schemaType)
      ? `pub_${f.key}`
      : f.key;
  });
  const filterParamNames = [...listingFieldParamNames, ...builtInFilterParamNames];

  // Note: currently, we only support nested enums with a single default filter
  //       that has schema type: "category"
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, params)
    : {};
  const isParamNameNestedEnumRelated = (paramName, key, isNestedEnum) => {
    return isNestedEnum && key ? paramName.indexOf(key) > -1 : false;
  };

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(params, filterConfigs);
  const paramEntries = Object.entries(unlimitedSearchParams);

  const listingFieldsAndBuiltInFilterParamNames = paramEntries.reduce((validParams, entry) => {
    const [paramName, paramValue] = entry;
    const isIndependentParam = filterParamNames.includes(paramName);
    const isNestedEnum = isIndependentParam
      ? false
      : isParamNameNestedEnumRelated(
          paramName,
          categorySearchConfig?.key,
          categorySearchConfig?.isNestedEnum
        );
    return isIndependentParam
      ? {
          ...validParams,
          ...validURLParamForExtendedData(
            paramName,
            paramValue,
            listingFieldFiltersConfig,
            defaultFiltersConfig
          ),
        }
      : dropNonFilterParams || isNestedEnum
      ? { ...validParams }
      : { ...validParams, [paramName]: paramValue };
  }, {});

  // TODO: Currently this only supports categoryLevel with nested param names.
  //       This needs more work to make other enum fields to understand nested keys.
  return { ...listingFieldsAndBuiltInFilterParamNames, ...validNestedCategoryParamNames };
};

/**
 * Helper to pick only valid values of search params from URL (location)
 * Note: location.search might look like: '?pub_category=men&pub_amenities=towels,bathroom'
 *
 * @param {Object} props object containing: location and (app) config
 * @returns picked search params against extended data config and default filter config
 */
export const validUrlQueryParamsFromProps = props => {
  const { location, config, params: currentPathParams = {} } = props;
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const listingCategories = config.categoryConfiguration.categories;
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams,
  };

  // Translate path-based category params (cat1/cat2/cat3) to pub_categoryLevel* keys
  const { cat1, cat2, cat3 } = currentPathParams;
  const categoryFromPath = {};
  if (cat1) categoryFromPath.pub_categoryLevel1 = cat1;
  if (cat2) categoryFromPath.pub_categoryLevel2 = cat2;
  if (cat3) categoryFromPath.pub_categoryLevel3 = cat3;

  // eslint-disable-next-line no-unused-vars
  const { mapSearch, page, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  // Merge path-based category params with query string params.
  // Path params take precedence (they define the category context).
  const mergedParams = { ...searchInURL, ...categoryFromPath };
  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
  return validFilterParams(mergedParams, filterConfigs, false);
};

/**
 * Helper to figure out initialValues for Final Form that handles search filters
 *
 * @param {Object} props object containing: location, listingFieldsConfig, defaultFiltersConfig
 * @param {Object} currentQueryParams object containing current state of queryParams (used only when isLiveEdit is false)
 * @returns a function with params queryParamNames, and isLiveEdit.
 *          It's called from FilterComponent and it returns initial values for the filter.
 */
export const initialValues = (props, currentQueryParams) => (queryParamNames, isLiveEdit) => {
  const urlQueryParams = validUrlQueryParamsFromProps(props);

  // Get initial value for a given parameter from state if its there.
  const getInitialValue = paramName => {
    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParam = currentQueryParams[paramName];
    const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
    return hasQueryParamInState && !isLiveEdit ? currentQueryParam : urlQueryParams[paramName];
  };

  // Return all the initial values related to given queryParamNames
  // InitialValues for "amenities" filter could be
  // { amenities: "has_any:towel,jacuzzi" }
  const isArray = Array.isArray(queryParamNames);
  return isArray
    ? queryParamNames.reduce((acc, paramName) => {
        const initValue = getInitialValue(paramName);
        const addInitialValueMaybe = initValue ? { [paramName]: initValue } : {};
        return { ...acc, ...addInitialValueMaybe };
      }, {})
    : {};
};

/**
 * Some parameters could conflict with sort. If sortConfig defines conflictingFilters,
 * This function checks if they are active and returns "sort" param as null
 *
 * @param {*} searchParams
 * @param {*} filterConfigs contains config like listingFieldsConfig and defaultFiltersConfig
 * @param {*} sortConfig
 * @returns sort parameter as null if sortConfig defines conflictingFilters
 */
export const cleanSearchFromConflictingParams = (searchParams, filterConfigs, sortConfig) => {
  // Single out filters that should disable SortBy when an active
  // keyword search sorts the listings according to relevance.
  // In those cases, sort parameter should be removed.
  const sortingFiltersActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    searchParams,
    filterConfigs
  );

  // search params without category-restricted params
  const unlimitedSearchParams = omitLimitedListingFieldParams(searchParams, filterConfigs);

  return sortingFiltersActive
    ? { ...unlimitedSearchParams, [sortConfig.queryParamName]: null }
    : unlimitedSearchParams;
};

/**
 * Extract search parameters, including a custom URL params,
 * which are validated by mapping the values to marketplace custom config.
 *
 * @param {Object} params Search query params
 * @param {Object} listingFieldsConfig extended data configuration with indexForSearch === true
 * @param {Object} defaultFiltersConfig configuration for default built-in filters.
 * @param {Object} sortConfig config for sort search results feature
 * @param {boolean} isOriginInUse if origin is in use, return it too.
 */
export const pickSearchParamsOnly = (params, filterConfigs, sortConfig, isOriginInUse) => {
  const { address, origin, bounds, ...rest } = params || {};
  const boundsMaybe = bounds ? { bounds } : {};
  const originMaybe = isOriginInUse && origin ? { origin } : {};
  const filterParams = validFilterParams(rest, filterConfigs);
  const sort = rest[sortConfig.queryParamName];
  const sortMaybe = sort ? { sort } : {};

  return {
    ...boundsMaybe,
    ...originMaybe,
    ...filterParams,
    ...sortMaybe,
  };
};

/**
 * This helper has 2 functions:
 * - It picks search params from Location instance (location.search)
 * - It verifies that those search params are the same as search params in state.
 *   In some cases, search params are referencing previous params
 *   and listings should not be considered loaded.
 *
 * @param {Object} searchFromLocation searchParams from URL (location.search)
 * @param {Object} searchParamsInProps searchParams from store
 * @param {Object} listingFieldsConfig config for listing's extended data
 * @param {Object} defaultFiltersConfig config for default filters
 * @param {Object} sortConfig config for SortBy feature
 * @returns object containing
 *   1. searchParamsInURL (omit pagination 'page' or 'mapSearch'),
 *   2. urlQueryParams (picked valid search params for listing query), and
 *   3. searchParamsAreInSync is true if searchFromLocation and searchParamsInProps match.
 */
export const searchParamsPicker = (
  searchFromLocation,
  searchParamsInProps,
  filterConfigs,
  sortConfig,
  isOriginInUse,
  currentPathParams = {}
) => {
  // Merge path-based category params into parsed query string
  const { cat1, cat2, cat3 } = currentPathParams;
  const categoryFromPath = {};
  if (cat1) categoryFromPath.pub_categoryLevel1 = cat1;
  if (cat2) categoryFromPath.pub_categoryLevel2 = cat2;
  if (cat3) categoryFromPath.pub_categoryLevel3 = cat3;

  const { mapSearch, page, ...parsedSearch } = parse(searchFromLocation, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const searchParamsInURL = { ...parsedSearch, ...categoryFromPath };

  // Pick only search params that are part of current search configuration
  const queryParamsFromSearchParams = pickSearchParamsOnly(
    searchParamsInProps,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );
  // Pick only search params that are part of current search configuration
  const queryParamsFromURL = pickSearchParamsOnly(
    searchParamsInURL,
    filterConfigs,
    sortConfig,
    isOriginInUse
  );

  // Page transition might initially use values from previous search
  const searchParamsAreInSync =
    stringify(queryParamsFromURL) === stringify(queryParamsFromSearchParams);

  return {
    urlQueryParams: queryParamsFromURL,
    searchParamsInURL,
    searchParamsAreInSync,
  };
};

export const pickListingFieldFilters = params => {
  const {
    listingFields,
    locationSearch,
    categoryConfiguration,
    activeListingTypes,
    currentPathParams = {},
  } = params;
  const parsedSearch = parse(locationSearch);

  // Merge path-based category params into search params
  const { cat1, cat2, cat3 } = currentPathParams;
  const categoryFromPath = {};
  if (cat1) categoryFromPath.pub_categoryLevel1 = cat1;
  if (cat2) categoryFromPath.pub_categoryLevel2 = cat2;
  if (cat3) categoryFromPath.pub_categoryLevel3 = cat3;
  const searchParams = { ...parsedSearch, ...categoryFromPath };

  const categories = categoryConfiguration.categories;
  const validNestedCategoryParamNames = categories
    ? validURLParamForCategoryData(categoryConfiguration.key, categories, 1, searchParams)
    : {};

  const validListingTypeParamNames = activeListingTypes
    ? validURLParamForListingTypeData(activeListingTypes, searchParams)
    : {};

  const currentCategories = Object.values(validNestedCategoryParamNames);
  const currentListingType = Object.values(validListingTypeParamNames);
  const pickedFields = listingFields.reduce((picked, fieldConfig) => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    const isTargetListingField = isFieldForListingType(currentListingType, fieldConfig);
    return isTargetCategory && isTargetListingField ? [...picked, fieldConfig] : picked;
  }, []);
  return pickedFields;
};

const parseRangeValue = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const parts = value.split(',');
  return parts.length === 2 ? parts.map(str => Number.parseInt(str, 10)) : null;
};

export const parseBuyerCanopyRange = queryParams => {
  if (!queryParams) {
    return null;
  }

  const minParam = queryParams?.pub_allowedRangeMin;
  const maxParam = queryParams?.pub_allowedRangeMax;
  const hasBuyerSetRange =
    typeof minParam === 'string' && minParam.length > 0 && typeof maxParam === 'string' && maxParam.length > 0;

  if (!hasBuyerSetRange) {
    return null;
  }

  const parsedMax = parseRangeValue(minParam);
  const parsedMin = parseRangeValue(maxParam);

  if (!parsedMax || !parsedMin) {
    return null;
  }

  const buyerMax = parsedMax[1];
  const buyerMin = parsedMin[0];

  const isValidMin = Number.isFinite(buyerMin);
  const isValidMax = Number.isFinite(buyerMax);
  const isValidRange = isValidMin && isValidMax && buyerMin <= buyerMax;

  return isValidRange ? { min: buyerMin, max: buyerMax } : null;
};

const listingMatchesBuyerCanopyRange = (listing, range) => {
  if (!range) {
    return true;
  }

  const listingMin = listing?.attributes?.publicData?.allowedRangeMin;
  const listingMax = listing?.attributes?.publicData?.allowedRangeMax;

  if (!Number.isFinite(listingMin) || !Number.isFinite(listingMax)) {
    return false;
  }

  return listingMin <= range.min && listingMax >= range.max;
};

export const filterListingsByBuyerCanopyRange = (listings = [], range) => {
  if (!range) {
    return listings;
  }

  return listings.filter(listing => listingMatchesBuyerCanopyRange(listing, range));
};

/**
 * Returns listing fields (extended data configs) grouped into arrays. [primaryConfigArray, secondaryConfigArray]
 * @param {Object} configs listing extended data config
 * @param {Array<String>} activeListingTypes select configs that are marked only for these active listing types
 * @returns Array of grouped arrays. First subarray contains primary configs and the second contains secondary configs.
 */
export const groupListingFieldConfigs = (configs, activeListingTypes) =>
  configs.reduce(
    (grouped, config) => {
      const [primary, secondary] = grouped;
      const { listingTypeConfig = {}, filterConfig } = config;
      const isIndexed = filterConfig?.indexForSearch === true;
      const isActiveListingTypes =
        !listingTypeConfig.limitToListingTypeIds ||
        listingTypeConfig.listingTypeIds.some(lt => activeListingTypes.includes(lt));
      const isPrimary = filterConfig?.group === 'primary';
      return isActiveListingTypes && isIndexed && isPrimary
        ? [[...primary, config], secondary]
        : isActiveListingTypes && isIndexed
        ? [primary, [...secondary, config]]
        : grouped;
    },
    [[], []]
  );

// SEO meta data per category page.
// Keys match the Sharetribe category IDs from listing-categories.json.
// Each entry can define title and description overrides.
// {category} is replaced with the category display name from the trail.
// {brand} is replaced with the L2 name. {model} with the L3 name.
const CATEGORY_SEO = {
  // L1 categories
  'complete-systems': {
    title: 'Used Skydiving Rigs & Complete Systems for Sale | Avento',
    description:
      'Find used complete skydiving systems — rig, main canopy, reserve, and AAD in one package. Great deals from top brands with safe escrow payments on Avento.',
  },
  rigs: {
    title: 'Used Skydiving Containers for Sale | Avento',
    description:
      'Browse used skydiving rigs, containers, and harness systems from UPT, Sun Path, Mirage, and more. Compare prices and buy with safe escrow payments on Avento.',
  },
  'main-canopies': {
    title: 'Used Skydiving Canopies & Parachutes for Sale | Avento',
    description:
      'Buy used main canopies from Performance Designs, JYRO, Aerodyne, Fluid Wings, and more. Different sizes and skill levels — buy with escrow payment protection on Avento.',
  },
  reserves: {
    title: 'Used Reserve Parachutes for Sale | Avento',
    description:
      'Find used reserve canopies from PD, Aerodyne, Icarus World, and more. Compare sizes and prices — buy with safe escrow payments on Avento.',
  },
  aad: {
    title: 'Used AADs for Sale — Cypres, Vigil & More | Avento',
    description:
      'Buy used automatic activation devices — Cypres, Vigil, and MarS. Check service life, compare prices, and purchase with escrow payment protection on Avento.',
  },
  altimeters: {
    title: 'Used Skydiving Altimeters for Sale | Avento',
    description:
      'Find used digital and analogue skydiving altimeters from L&B, Alti-2, AON2, and more. GPS, logbook, and wrist-mount options on Avento.',
  },
  audibles: {
    title: 'Used Audible Altimeters for Sale | Avento',
    description:
      'Buy used audible altimeters from L&B, Alti-2, AON2, and more. Speaking and GPS models available — buy with safe payments on Avento.',
  },
  wingsuits: {
    title: 'Used Wingsuits for Sale | Avento',
    description:
      'Find used wingsuits from Squirrel, Phoenixfly, Intrudair, and more. Beginner to competition level — buy and sell with safe payments on Avento.',
  },
  tracksuits: {
    title: 'Used Skydiving Tracksuits for Sale | Avento',
    description:
      'Browse used one-piece and two-piece tracking suits from Squirrel, Intrudair, Pressurized, and more. Buy with escrow payment protection on Avento.',
  },
  'helmets-and-equipment': {
    title: 'Used Skydiving Helmets & Equipment for Sale | Avento',
    description:
      'Find used skydiving helmets and equipment from Cookie, Tonfly, Bonehead, and more. Full face, open face, and camera helmets, buy with safe payments on Avento.',
  },
  clothing: {
    title: 'Used Skydiving Suits & Apparel for Sale | Avento',
    description:
      'Buy used skydiving suits — freefly, formation, tunnel, swooppants, and more. Buy and sell with safe escrow payments on Avento.',
  },
  'videography-photography': {
    title: 'Skydiving Camera Equipment for Sale | Avento',
    description:
      'Find skydiving camera gear — cameras, helmet mounts, and accessories. Buy and sell with safe escrow payments on Avento.',
  },
  'other-gear': {
    title: 'Used Skydiving Accessories & Parts for Sale | Avento',
    description:
      'Browse pilot chutes, risers, toggles, sliders, D-bags, and more. Find the parts you need and buy with safe payments on Avento.',
  },
  base: {
    title: 'Used BASE Jumping Gear for Sale | Avento',
    description:
      'Find used BASE jumping gear — containers, canopies, and complete systems from Squirrel, Adrenaline BASE, Atair Canopies, and more. Buy with escrow payment protection on Avento.',
  },

  // L2 — Container brands
  'rigs-upt': {
    title: 'Used UPT Rigs for Sale — Vector, Micron & More | Avento',
    description:
      'Great deals on used UPT rigs — Vector, Micron, Sigma, and Mutant. Compare sizes and prices with safe escrow payments on Avento.',
  },
  'rigs-sun-path': {
    title: 'Used Sun Path Javelin Rigs for Sale | Avento',
    description:
      'Browse used Sun Path Javelin rigs — Odyssey and Accuracy models. Compare sizes, check prices, and buy with safe payments on Avento.',
  },
  'rigs-mirage-systems': {
    title: 'Used Mirage Rigs for Sale — G4, G3 & More | Avento',
    description:
      'Find used Mirage rigs — G4, G3, and W-Series wingsuit containers. Compare prices and buy with safe escrow payments on Avento.',
  },

  // L2 — Main canopy brands
  'main-performance-designs': {
    title: 'Used Performance Designs Canopies for Sale | Avento',
    description:
      'Great deals on used PD canopies — Sabre, Stiletto, Katana, Velocity, Valkyrie, and more. Different sizes available with safe escrow payments on Avento.',
  },
  'main-jyro': {
    title: 'Used JYRO Canopies for Sale — Safire, Crossfire & More | Avento',
    description:
      'Find used JYRO (Icarus) canopies — Safire, Crossfire, JFX2, Leia, and more. Different sizes and levels with escrow payment protection on Avento.',
  },
  'main-aerodyne-research': {
    title: 'Used Aerodyne Canopies for Sale — Pilot, Zulu & More | Avento',
    description:
      'Browse used Aerodyne main canopies — Pilot, Pilot7, Solo, Zulu, and more. Compare sizes and prices on Avento.',
  },
  'main-icarus-world': {
    title: 'Used Icarus World Canopies for Sale | Avento',
    description:
      'Find used Icarus World canopies — Equinox, Safire, Crossfire, OM-7, and more. Buy with safe escrow payments on Avento.',
  },
  'main-fluid-wings': {
    title: 'Used Fluid Wings Canopies for Sale | Avento',
    description:
      'Buy used Fluid Wings canopies — Gangster, Echo, HK, Prime, and more. High-performance parachutes with safe payments on Avento.',
  },

  // L2 — Product types & niche
  'altimeters-digital': {
    title: 'Used Digital Skydiving Altimeters for Sale | Avento',
    description:
      'Find used digital altimeters from L&B, Alti-2, AON2, and more. GPS, logbook, and waterproof options — buy with safe payments on Avento.',
  },
  'audibles-lb': {
    title: 'Used L&B Audible Altimeters for Sale | Avento',
    description:
      'Find used L&B audibles — Optima, Protrack, Quattro, and Solo. Compare prices and buy with safe escrow payments on Avento.',
  },
  'base-containerandcanopy': {
    title: 'Used Complete BASE Systems for Sale | Avento',
    description:
      'Find used complete BASE rigs — container and canopy setups from Squirrel, Adrenaline BASE, Atair Canopies, and more. Buy with escrow payment protection on Avento.',
  },
  'helmets-and-equipment-helmets': {
    title: 'Used Skydiving Helmets for Sale | Avento',
    description:
      'Find used skydiving helmets from Cookie, Tonfly, SkyHelmets, Bonehead, and Kiss. Full face, open face, and camera helmets with safe payments on Avento.',
  },
  'wingsuits-squirrel': {
    title: 'Used Squirrel Wingsuits for Sale | Avento',
    description:
      'Buy used Squirrel wingsuits, from beginner-friendly to high-performance models. Compare prices and buy with safe escrow payments on Avento.',
  },

  // L3 — Container models
  'rigs-upt-vector': {
    title: 'Used UPT Vector for Sale | Avento',
    description:
      'Find used UPT Vector containers in sizes like V306, V310, and V348. Compare prices and buy with safe escrow payments on Avento.',
  },

  // L3 — PD models
  'main-performance-designs-sabre-2': {
    title: 'Used PD Sabre 2 for Sale | Avento',
    description:
      'Find used Performance Designs Sabre 2 canopies — 9-cell intermediate in different sizes. Compare prices and buy with safe escrow payments on Avento.',
  },
  'main-performance-designs-sabre-3': {
    title: 'Used PD Sabre 3 for Sale | Avento',
    description:
      'Buy used Performance Designs Sabre 3 canopies — latest generation 9-cell intermediate. Compare sizes and prices with safe payments on Avento.',
  },
  'main-performance-designs-stiletto': {
    title: 'Used PD Stiletto for Sale | Avento',
    description:
      'Find used Performance Designs Stiletto canopies — fully elliptical 9-cell in different sizes. Great deals with escrow payment protection on Avento.',
  },
  'main-performance-designs-velocity': {
    title: 'Used PD Velocity for Sale | Avento',
    description:
      'Buy used Performance Designs Velocity canopies — cross-braced high-performance in different sizes. Compare prices with safe escrow payments on Avento.',
  },
  'main-performance-designs-valkyrie': {
    title: 'Used PD Valkyrie for Sale | Avento',
    description:
      'Find used Performance Designs Valkyrie canopies, crossbraced high-performance wings in different sizes. Buy with safe escrow payments on Avento.',
  },
  'main-performance-designs-storm': {
    title: 'Used PD Storm for Sale | Avento',
    description:
      'Find used Performance Designs Storm canopies, a 7-cell known for reliable openings. Compare sizes and buy with safe escrow payments on Avento.',
  },
  'main-performance-designs-pulse': {
    title: 'Used PD Pulse for Sale | Avento',
    description:
      'Buy used Performance Designs Pulse canopies, a lightly loaded 9-cell with low pack volume. Compare sizes and prices with safe payments on Avento.',
  },
  'main-performance-designs-katana': {
    title: 'Used PD Katana for Sale | Avento',
    description:
      'Find used Performance Designs Katana canopies, a fully elliptical 9-cell for experienced pilots. Great deals with escrow payment protection on Avento.',
  },

  // L3 — JYRO models
  'main-jyro-safire-2': {
    title: 'Used JYRO Safire 2 for Sale | Avento',
    description:
      'Find used JYRO Safire 2 canopies — 9-cell intermediate in different sizes. Compare prices and buy with safe escrow payments on Avento.',
  },
  'main-jyro-safire-3': {
    title: 'Used JYRO Safire 3 for Sale | Avento',
    description:
      'Buy used JYRO Safire 3 canopies — latest generation 9-cell intermediate. Compare sizes and prices with safe payments on Avento.',
  },
  'main-jyro-leia': {
    title: 'Used JYRO Leia for Sale | Avento',
    description:
      'Find used JYRO Leia canopies — cross-braced competition canopy in different sizes. Great deals with escrow payment protection on Avento.',
  },
  'main-jyro-crossfire-3': {
    title: 'Used JYRO Crossfire 3 for Sale | Avento',
    description:
      'Buy used JYRO Crossfire 3 canopies — fully elliptical 9-cell for experienced jumpers. Compare sizes and buy with safe payments on Avento.',
  },
  'main-jyro-crossfire-2': {
    title: 'Used JYRO Crossfire 2 for Sale | Avento',
    description:
      'Find used JYRO Crossfire 2 canopies, a fully elliptical 9-cell for experienced jumpers. Compare prices and buy with safe escrow payments on Avento.',
  },
  'main-jyro-kraken': {
    title: 'Used JYRO Kraken for Sale | Avento',
    description:
      'Buy used JYRO Kraken canopies, a modern high-performance wing for experienced pilots. Compare sizes and buy with safe payments on Avento.',
  },

  // L3 — Other canopy models
  'main-aerodyne-research-pilot': {
    title: 'Used Aerodyne Pilot for Sale | Avento',
    description:
      'Find used Aerodyne Pilot canopies, a forgiving 9-cell intermediate in different sizes. Compare prices and buy with safe escrow payments on Avento.',
  },
  'main-icarus-world-xfire': {
    title: 'Used Icarus World X-Fire for Sale | Avento',
    description:
      'Find used Icarus World X-Fire canopies, high-performance elliptical wings in different sizes. Buy with safe escrow payments on Avento.',
  },

  // L3 — Helmet brands
  'helmets-and-equipment-helmets-cookie': {
    title: 'Used Cookie Helmets for Sale | Avento',
    description:
      'Buy used Cookie skydiving helmets. G4, G35, M3, and Fuel models with safe escrow payments on Avento.',
  },
};

/**
 * Build a BreadcrumbList schema from the current category trail.
 * Returns null when no category is selected: on the plain /s page the visible
 * breadcrumb is a single element and one-item lists are invalid for Google.
 *
 * The schema mirrors the visible breadcrumb exactly: the first element is the
 * brand root ("Avento", linking /s) and the current page comes last without an
 * item URL (Google then uses the page URL).
 */
export const buildSearchBreadcrumbSchema = (categoryTrail, marketplaceRootURL) => {
  if (!categoryTrail || categoryTrail.length === 0) return null;

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: SEARCH_BREADCRUMB_ROOT_LABEL,
      item: `${marketplaceRootURL}/s`,
    },
  ];

  categoryTrail.forEach((crumb, index) => {
    const isLast = index === categoryTrail.length - 1;
    const pathSegments = categoryTrail
      .slice(0, index + 1)
      .map(c => c.id)
      .join('/');
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: crumb.label,
      ...(isLast ? {} : { item: `${marketplaceRootURL}/s/${pathSegments}` }),
    });
  });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

// Resolves the selected category trail from URL query params into
// [{ id, label }] using the category names from the hosted config.
// SearchPageWithGrid overlays the curated SEO labels on top before the trail
// is displayed or turned into schema; the content module is intentionally not
// imported here to keep it out of the bundles of other shared.js consumers.
export const categoryTrailFromParams = (queryParams = {}, categoryConfiguration = {}) => {
  const categories = categoryConfiguration?.categories || [];
  const levelKeys = categoryConfiguration?.categoryLevelKeys || [];

  const trail = [];
  let currentLevel = categories;

  levelKeys.forEach(levelKey => {
    const queryKey = `pub_${levelKey}`;
    const selectedId = queryParams[queryKey];
    if (!selectedId) {
      return;
    }

    const match = currentLevel?.find(category => category.id === selectedId);
    if (match) {
      trail.push({ id: match.id, label: match.name.trim() });
      currentLevel = match.subcategories || [];
    } else {
      currentLevel = [];
    }
  });

  return trail;
};

// Returns the deepest category ID from the search query params.
const getDeepestCategoryId = (mainSearchData = {}) => {
  return (
    mainSearchData.pub_categoryLevel3 ||
    mainSearchData.pub_categoryLevel2 ||
    mainSearchData.pub_categoryLevel1 ||
    null
  );
};

export const createSearchResultSchema = (
  listings,
  mainSearchData,
  intl,
  routeConfiguration,
  config,
  categoryTrail
) => {
  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const marketplaceName = config.marketplaceName;
  const { address, keywords } = mainSearchData;
  const keywordsMaybe = keywords ? `"${keywords}"` : null;

  // Use category-specific SEO data if available, otherwise fall back to defaults
  const categoryId = getDeepestCategoryId(mainSearchData);
  const categorySeo = categoryId ? CATEGORY_SEO[categoryId] : null;

  const searchTitle =
    address || keywordsMaybe || intl.formatMessage({ id: 'SearchPage.schemaForSearch' });

  const schemaTitle = categorySeo?.title
    ? categorySeo.title
    : intl.formatMessage({ id: 'SearchPage.schemaTitle' }, { searchTitle, marketplaceName });

  const schemaDescription = categorySeo?.description
    ? categorySeo.description
    : intl.formatMessage({ id: 'SearchPage.schemaDescription' });

  const schemaListings = listings.map((l, i) => {
    const title = l.attributes.title;
    const pathToItem = createResourceLocatorString('ListingPage', routeConfiguration, {
      id: l.id.uuid,
      slug: createSlug(title),
    });
    return {
      '@type': 'ListItem',
      position: i + 1,
      url: `${config.marketplaceRootURL}${pathToItem}`,
      name: title,
    };
  });

  const schemaMainEntity = {
    '@type': 'ItemList',
    name: searchTitle,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: schemaListings,
  };

  const searchResultsSchema = {
    '@type': 'SearchResultsPage',
    description: schemaDescription,
    name: schemaTitle,
    mainEntity: [schemaMainEntity],
  };

  const breadcrumb = buildSearchBreadcrumbSchema(categoryTrail, config.marketplaceRootURL);
  const schema = breadcrumb ? [searchResultsSchema, breadcrumb] : searchResultsSchema;

  return {
    title: schemaTitle,
    description: schemaDescription,
    schema,
  };
};

export const getDatesAndSeatsMaybe = (currentParams, newParams) => {
  const { seats, dates: newDates } = newParams;
  const { dates: currentDates } = currentParams;

  // Determine which dates and seats to use:
  // - if newDates has a value, it was just selected
  // - if newDates is null, it was just cleared
  // - if newDates is undefined, it was not modified, and we use currentDates
  const dates = !!newDates || newDates === null ? newDates : currentDates;

  const today = stringifyDateToISO8601(new Date());
  const aWeekFromNow = stringifyDateToISO8601(addTime(today, 7, 'day'));
  // Get parameters for dates and seats:
  // - If both dates and seats are included, pass both
  // - Dates can be queried without seats
  // - Seats cannot be queried without dates – pass a default date range
  //   of one week with the provided seats value
  // - If neither dates nor seats exist, set them to null to clear them from search
  const datesAndSeatsMaybe =
    dates && seats
      ? { dates, seats }
      : dates
      ? { dates }
      : seats
      ? { seats, dates: `${today},${aWeekFromNow}` }
      : { seats: null, dates: null };
  return datesAndSeatsMaybe;
};

/**
 * Returns params for createResourceLocatorString function based on the current
 * location and route configuration
 * @param {*} routes current route configuration
 * @param {*} location current ReactRouter location
 * @returns an object with the attributes routeName and pathParams, which can then be passed
 * as the corresponding parameters to createResourceLocatorString
 */
export const getSearchPageResourceLocatorStringParams = (routes, location) => {
  const matchedRoutes = matchPathname(location.pathname, routes);
  const searchPageRouteNames = new Set([
    'SearchPage',
    'SearchPageCat1',
    'SearchPageCat2',
    'SearchPageCat3',
  ]);

  if (matchedRoutes.length > 0) {
    const matched = matchedRoutes[0];
    const { params: pathParams, route } = matched;
    const routeName = searchPageRouteNames.has(route.name) ? route.name : 'SearchPage';

    return {
      routeName,
      pathParams,
    };
  } else {
    console.error(`Route not found for pathname ${location.pathname}, redirecting to SearchPage`);
    return {
      routeName: 'SearchPage',
      pathParams: {},
    };
  }
};

/**
 * Build a search page URL string, splitting category params into path segments
 * and keeping everything else as query params.
 *
 * @param {Array} routes - Route configuration
 * @param {Object} allParams - All search params (may include pub_categoryLevel*)
 * @returns {string} The URL string e.g. "/s/main-canopies?sort=createdAt"
 */
export const createSearchPageUrl = (routes, allParams) => {
  const { pub_categoryLevel1, pub_categoryLevel2, pub_categoryLevel3, ...queryParams } =
    allParams || {};

  const pathParams = {};
  let routeName = 'SearchPage';
  if (pub_categoryLevel1) {
    pathParams.cat1 = pub_categoryLevel1;
    routeName = 'SearchPageCat1';
  }
  if (pub_categoryLevel2) {
    pathParams.cat2 = pub_categoryLevel2;
    routeName = 'SearchPageCat2';
  }
  if (pub_categoryLevel3) {
    pathParams.cat3 = pub_categoryLevel3;
    routeName = 'SearchPageCat3';
  }

  return createResourceLocatorString(routeName, routes, pathParams, queryParams);
};

export const getActiveListingTypes = (config, listingTypePathParam) => {
  const availableListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const activeListingTypes = listingTypePathParam
    ? availableListingTypes.filter(lt => lt === listingTypePathParam)
    : availableListingTypes;
  return { activeListingTypes };
};
